import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { X } from "lucide-react";
import { useTheme } from "../../../../core/providers/ThemeProvider";
import { DialogWrapper } from "./DialogWrapper";
import acceptIcon from "../../../../assets/accept.svg";
import toggleContact from "../../../../assets/toggleContact.svg";

interface EditCartResultDialogProps {
  open: boolean;
  onClose: () => void;
  variant: "approved" | "rejected";
  onViewDetails?: () => void;
  orderDetails?: {
    orderCode?: string;
    senderName?: string;
  };
}

const EditCartResultDialog: React.FC<EditCartResultDialogProps> = ({
  open,
  onClose,
  variant,
  onViewDetails,
  orderDetails,
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.colors?.primary || "#2B7A4B";
  const isApproved = variant === "approved";

  const icon = isApproved ? acceptIcon : toggleContact;
  const title = isApproved ? "Edit Request Approved!" : "Edit Request Rejected";
  const notificationText = isApproved
    ? "The sender has been notified of your approval."
    : "The sender has been notified of your decision.";

  const messageWithName = isApproved
    ? (
        <>
          You have successfully approved the edit request from{" "}
          <strong>{orderDetails?.senderName}</strong>. The order has been updated with the new values.
        </>
      )
    : (
        <>
          You have rejected the edit request from{" "}
          <strong>{orderDetails?.senderName}</strong>. The order will remain unchanged with the original values.
        </>
      );

  const messageWithoutName = isApproved
    ? "You have successfully approved the edit request. The order has been updated with the new values."
    : "You have rejected the edit request. The order will remain unchanged with the original values.";

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="edit-cart-result-title"
      aria-describedby="edit-cart-result-description"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)",
          width: { xs: "95%", sm: "540px" },
          maxWidth: "540px",
        },
      }}
    >

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "32px 24px 24px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          sx={{
            position: "absolute",
            right: "16px",
            top: "16px",
            width: "44px",
            height: "44px",
            borderRadius: "8px",
            zIndex: 2,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <X size={24} color="#A4A7AE" />
        </IconButton>

        {/* Icon */}
        <Box
          component="img"
          src={icon}
          alt={isApproved ? "Accept" : "Reject"}
          sx={{
            width: "48px",
            height: "48px",
            mb: 3,
            mt: 1,
            position: "relative",
            zIndex: 1,
          }}
        />

        {/* Title and Message */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            width: "100%",
            textAlign: "center",
            mb: 3,
          }}
        >
          <Typography
            id="edit-cart-result-title"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: "22px",
              lineHeight: "32px",
              color: "#181D27",
            }}
          >
            {title}
          </Typography>
          <Typography
            id="edit-cart-result-description"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "21px",
              color: "#667071",
              px: 2,
            }}
          >
            {orderDetails?.senderName ? messageWithName : messageWithoutName}
          </Typography>

          {/* Order Details Card */}
          {orderDetails?.orderCode && (
            <Box
              sx={{
                width: "100%",
                mt: 2,
                p: 2.5,
                background: "#F9FAFB",
                borderRadius: "12px",
                border: "1px solid #E9EAEB",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    color: "#667071",
                  }}
                >
                  Order Code
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: primaryColor,
                  }}
                >
                  {orderDetails.orderCode}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "13px",
              color: "#667071",
              mt: 1,
            }}
          >
            {notificationText}
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            width: "100%",
            mt: 1,
          }}
        >
          {isApproved ? (
            <>
              <Button
                onClick={onClose}
                sx={{
                  flex: 1,
                  padding: "12px 20px",
                  height: "44px",
                  background: "#FFFFFF",
                  border: "1px solid #D9DADF",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#484A54",
                  textTransform: "none",
                  "&:hover": {
                    background: "#F5F5F5",
                  },
                }}
              >
                Close
              </Button>
              {onViewDetails && (
                <Button
                  onClick={onViewDetails}
                  sx={{
                    flex: 1,
                    padding: "12px 20px",
                    height: "44px",
                    background: primaryColor,
                    borderRadius: "8px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#FFFFFF",
                    textTransform: "none",
                    "&:hover": {
                      background: `${primaryColor}DD`,
                    },
                  }}
                >
                  View Details
                </Button>
              )}
            </>
          ) : (
            <Button
              onClick={onClose}
              sx={{
                flex: 1,
                padding: "12px 20px",
                height: "44px",
                background: primaryColor,
                borderRadius: "8px",
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "14px",
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background: `${primaryColor}DD`,
                },
              }}
            >
              Okay
            </Button>
          )}
        </Box>
      </Box>
    </DialogWrapper>
  );
};

export default EditCartResultDialog;
